/**
 * GarmentIssueDialog - Three-reason garment issue dialog at print station
 *
 * Reasons (mirrors catcher redo flow downstream):
 * - garment_contamination — stains, marks, lint, scorch
 * - manufacturer_defect   — holes, seam issues, dye flaws
 * - wrong_item_picked     — wrong blank pulled from shelf
 *
 * Two-phase: reason grid → confirm + optional notes (max 500 chars).
 * Scanner-proof: no autoFocus, Back focused on phase transition.
 * Numeric hotkeys 1-3 for reason selection, Enter to confirm.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Loader2 } from 'lucide-react';

export type GarmentIssueReason =
  | 'garment_contamination'
  | 'manufacturer_defect'
  | 'wrong_item_picked';

export const GARMENT_ISSUE_REASONS: ReadonlyArray<{
  value: GarmentIssueReason;
  labelKey: string;
  descriptionKey: string;
}> = [
  {
    value: 'garment_contamination',
    labelKey: 'printer.garmentIssue.contamination.label',
    descriptionKey: 'printer.garmentIssue.contamination.description',
  },
  {
    value: 'manufacturer_defect',
    labelKey: 'printer.garmentIssue.manufacturerDefect.label',
    descriptionKey: 'printer.garmentIssue.manufacturerDefect.description',
  },
  {
    value: 'wrong_item_picked',
    labelKey: 'printer.garmentIssue.wrongItem.label',
    descriptionKey: 'printer.garmentIssue.wrongItem.description',
  },
];

interface GarmentIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: GarmentIssueReason, notes?: string) => void;
  isPending: boolean;
  garment: {
    sku: string;
    productName: string;
    size: string | null;
    color: string | null;
  } | null;
}

export function GarmentIssueDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  garment,
}: GarmentIssueDialogProps) {
  const { t } = useTranslation('production');
  const [selectedReason, setSelectedReason] = useState<GarmentIssueReason | null>(null);
  const [notes, setNotes] = useState('');
  const backButtonRef = useRef<HTMLButtonElement>(null);

  // Track latest isPending so the keydown closure can guard against double-submit
  // even within the small render window before its effect re-registers.
  const isPendingRef = useRef(isPending);
  useEffect(() => {
    isPendingRef.current = isPending;
  }, [isPending]);

  // Focus Back when transitioning to confirm phase (scanner-proofing).
  // If Radix focus trap moves focus after the reason button unmounts, landing
  // on Confirm + an in-flight scanner Enter could fire the mutation.
  // Focusing Back means worst case = returning to reason grid.
  useEffect(() => {
    if (selectedReason && backButtonRef.current) {
      backButtonRef.current.focus();
    }
  }, [selectedReason]);

  // Reset state on close
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setSelectedReason(null);
        setNotes('');
      }
      onOpenChange(isOpen);
    },
    [onOpenChange],
  );

  // Prevent auto-focus on open (scanner-proofing)
  const handleOpenAutoFocus = useCallback((e: Event) => {
    e.preventDefault();
  }, []);

  // Keyboard hotkeys.
  // We intentionally don't depend on `isPending` here — re-registering the
  // listener on every mutation flip leaves a brief window with no handler
  // attached. The `isPendingRef` check inside the closure is the
  // authoritative double-submit guard.
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (isPendingRef.current) return;

      // Don't capture keys while typing in textarea
      const active = document.activeElement;
      if (active?.tagName.toLowerCase() === 'textarea') return;

      if (!selectedReason) {
        const num = parseInt(e.key, 10);
        const reason = num >= 1 && num <= GARMENT_ISSUE_REASONS.length
          ? GARMENT_ISSUE_REASONS[num - 1]
          : undefined;
        if (reason) {
          e.preventDefault();
          e.stopPropagation();
          setSelectedReason(reason.value);
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onConfirm(selectedReason, notes || undefined);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, selectedReason, notes, onConfirm]);

  const selectedMeta = selectedReason
    ? GARMENT_ISSUE_REASONS.find((r) => r.value === selectedReason)
    : null;

  if (!garment) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2.5">
            <AlertTriangle className="h-6 w-6 text-orange-500 shrink-0" aria-hidden="true" />
            {selectedReason
              ? t('printer.garmentIssue.confirmTitle')
              : t('printer.garmentIssue.selectTitle')}
          </DialogTitle>
        </DialogHeader>

        {isPending ? (
          <div className="flex flex-col items-center justify-center py-8 min-h-[280px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t('catcher.creatingRedo')}</p>
          </div>
        ) : selectedReason && selectedMeta ? (
          /* Phase 2: Confirm + optional notes */
          <div className="flex flex-col gap-4 py-4 min-h-[280px]">
            <div className="rounded-lg border border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/30 p-4 flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-orange-500 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-lg font-semibold text-orange-900 dark:text-orange-200">
                  {t(selectedMeta.labelKey)}
                </p>
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  {t(selectedMeta.descriptionKey)}
                </p>
                <p className="text-sm font-mono text-orange-600 dark:text-orange-400 mt-2">
                  {garment.sku}
                  {garment.size && `, ${garment.size}`}
                  {garment.color && ` / ${garment.color}`}
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="garment-issue-notes">
                {t('printer.garmentIssue.notesLabel')}
              </Label>
              <Textarea
                id="garment-issue-notes"
                placeholder={t('printer.garmentIssue.notesPlaceholder')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[80px] text-base"
              />
            </div>

            <Button
              variant="destructive"
              className="w-full h-16 text-xl font-semibold"
              onClick={() => onConfirm(selectedReason, notes || undefined)}
              disabled={isPending}
              aria-label={t('printer.garmentIssue.confirmAria', { reason: t(selectedMeta.labelKey) })}
            >
              {t('printer.garmentIssue.confirmRedo')}
              <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">Enter</kbd>
            </Button>

            <Button
              ref={backButtonRef}
              variant="outline"
              className="w-full h-14 text-lg"
              onClick={() => {
                setSelectedReason(null);
                setNotes('');
              }}
              disabled={isPending}
              aria-label={t('printer.garmentIssue.backAria')}
            >
              {t('common:back')}
            </Button>

            <Button
              variant="ghost"
              className="w-full h-14"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              {t('common:cancel')}
            </Button>
          </div>
        ) : (
          /* Phase 1: Reason selection grid */
          <div className="flex flex-col">
            <div className="max-h-[60vh] overflow-y-auto py-4">
              <div className="grid grid-cols-1 gap-3">
                {GARMENT_ISSUE_REASONS.map((reason, index) => (
                  <Button
                    key={reason.value}
                    variant="outline"
                    className="h-auto min-h-[5rem] flex flex-col items-center justify-center text-center p-3 whitespace-normal hover:border-destructive hover:bg-destructive/5 relative"
                    onClick={() => setSelectedReason(reason.value)}
                  >
                    <kbd className="absolute top-2 left-2 text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded min-w-[20px] text-center">
                      {index + 1}
                    </kbd>
                    <span className="font-semibold text-base w-full">
                      {t(reason.labelKey)}
                    </span>
                    <span className="text-xs text-muted-foreground mt-1 leading-tight w-full">
                      {t(reason.descriptionKey)}
                    </span>
                  </Button>
                ))}
              </div>
            </div>

            <Button
              variant="ghost"
              className="w-full h-14 shrink-0"
              onClick={() => handleOpenChange(false)}
            >
              {t('common:cancel')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
